import React, { useEffect, useMemo, useState } from "react";
import { Loader2, Plus, ClipboardList, Check, Trash2, CalendarClock } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { listTasks, createTask, updateTask, deleteTask, TASK_ASSIGNEES } from "@/lib/api";

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }); }
  catch { return iso; }
};
const isOverdue = (iso, completed) => {
  if (!iso || completed) return false;
  try { return new Date(iso + "T00:00:00").getTime() < new Date().setHours(0,0,0,0); }
  catch { return false; }
};

const TaskDialog = ({ open, onClose, onSaved }) => {
  const [title, setTitle] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignee, setAssignee] = useState("shared");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) { setTitle(""); setDueDate(""); setAssignee("shared"); setNotes(""); }
  }, [open]);

  const save = async () => {
    if (!title.trim()) { toast.error("Title required."); return; }
    setSaving(true);
    try {
      await createTask({ title: title.trim(), due_date: dueDate || null, assignee, notes: notes || null });
      toast.success("Task created.");
      onSaved?.(); onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to create task.");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="task-dialog">
        <DialogHeader>
          <DialogTitle>New task</DialogTitle>
          <DialogDescription>Create a personal reminder or shared to-do.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-4 py-2">
          <div><Label>Title *</Label><Input value={title} onChange={(e) => setTitle(e.target.value)} data-testid="task-title" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label>Due date</Label>
              <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} data-testid="task-due" />
            </div>
            <div>
              <Label>Assignee</Label>
              <Select value={assignee} onValueChange={setAssignee}>
                <SelectTrigger data-testid="task-assignee"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {TASK_ASSIGNEES.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div><Label>Notes</Label><Textarea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="task-notes" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="task-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}Create
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default function TasksPanel({ onSessionExpired }) {
  const [loading, setLoading] = useState(true);
  const [tasks, setTasks] = useState([]);
  const [filter, setFilter] = useState("open");
  const [open, setOpen] = useState(false);

  const fetchAll = async () => {
    setLoading(true);
    try {
      const data = await listTasks();
      setTasks(data || []);
    } catch (err) {
      if (err?.response?.status === 401) { onSessionExpired?.(); return; }
      toast.error("Failed to load tasks.");
    } finally { setLoading(false); }
  };
  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const filtered = useMemo(() => {
    if (filter === "open") return tasks.filter((t) => !t.completed);
    if (filter === "done") return tasks.filter((t) => t.completed);
    if (filter === "overdue") return tasks.filter((t) => isOverdue(t.due_date, t.completed));
    return tasks;
  }, [tasks, filter]);

  const counts = useMemo(() => ({
    all: tasks.length,
    open: tasks.filter((t) => !t.completed).length,
    overdue: tasks.filter((t) => isOverdue(t.due_date, t.completed)).length,
    done: tasks.filter((t) => t.completed).length,
  }), [tasks]);

  const toggle = async (t) => {
    setTasks((arr) => arr.map((x) => x.id === t.id ? { ...x, completed: !t.completed } : x));
    try { await updateTask(t.id, { completed: !t.completed }); }
    catch { toast.error("Failed to update."); fetchAll(); }
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this task?")) return;
    try { await deleteTask(id); toast.success("Deleted."); fetchAll(); }
    catch { toast.error("Failed to delete."); }
  };

  return (
    <div className="space-y-6" data-testid="tasks-panel">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-medium mb-1" style={{ color: "#7A5E12" }}>Operations</div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold" style={{ color: "#0B2545" }}>Tasks &amp; Notes</h1>
          <p className="mt-1 text-sm" style={{ color: "#64748B" }}>Personal reminders and team to-dos.</p>
        </div>
        <Button onClick={() => setOpen(true)} style={{ background: "#0B2545", color: "#FFFFFF" }} data-testid="tasks-new">
          <Plus className="h-4 w-4 mr-2" /> New task
        </Button>
      </div>

      <div className="flex flex-wrap gap-1.5">
        {[
          { id: "open", label: `Open (${counts.open})` },
          { id: "overdue", label: `Overdue (${counts.overdue})` },
          { id: "done", label: `Done (${counts.done})` },
          { id: "all", label: `All (${counts.all})` },
        ].map((c) => (
          <button key={c.id} onClick={() => setFilter(c.id)}
                  data-testid={`tasks-filter-${c.id}`}
                  className="text-xs px-3 py-1.5 rounded-full border transition-colors"
                  style={{
                    background: filter === c.id ? "#0B2545" : "#FFFFFF",
                    color: filter === c.id ? "#FFD774" : "#475569",
                    borderColor: filter === c.id ? "#0B2545" : "#E2E8F0",
                  }}>
            {c.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="grid place-items-center py-20"><Loader2 className="h-6 w-6 animate-spin" style={{ color: "#0B2545" }} /></div>
      ) : filtered.length === 0 ? (
        <div className="rounded-2xl border-2 border-dashed py-16 text-center" style={{ borderColor: "#E2E8F0", color: "#64748B" }}>
          <ClipboardList className="h-8 w-8 mx-auto mb-3" style={{ color: "#94A3B8" }} />
          <p className="font-medium" style={{ color: "#0B2545" }}>No tasks</p>
          <p className="text-sm mt-1">Add one to get started.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="tasks-list">
          {filtered.map((t) => {
            const overdue = isOverdue(t.due_date, t.completed);
            return (
              <li key={t.id} className="rounded-xl border bg-white p-3 flex items-start gap-3"
                  style={{ borderColor: overdue ? "#FECACA" : "#E2E8F0" }}
                  data-testid={`task-${t.id}`}>
                <Checkbox checked={t.completed} onCheckedChange={() => toggle(t)} className="mt-0.5" data-testid={`task-check-${t.id}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className={`text-sm font-medium ${t.completed ? "line-through" : ""}`} style={{ color: t.completed ? "#94A3B8" : "#0B2545" }}>
                      {t.title}
                    </span>
                    {t.due_date && (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                            style={{ background: overdue ? "rgba(155,28,28,0.10)" : "#F1F5F9",
                                     color: overdue ? "#9B1C1C" : "#475569" }}>
                        <CalendarClock className="h-3 w-3" /> {fmtDate(t.due_date)}
                      </span>
                    )}
                    <span className="text-[10px] uppercase tracking-wider px-1.5 py-0.5 rounded"
                          style={{ background: "rgba(212,175,55,0.18)", color: "#7A5E12" }}>
                      {t.assignee}
                    </span>
                    {t.link_label && (
                      <span className="text-[10px] truncate max-w-[200px]" style={{ color: "#64748B" }}>↳ {t.link_label}</span>
                    )}
                  </div>
                  {t.notes && <div className="text-xs mt-1" style={{ color: "#64748B" }}>{t.notes}</div>}
                </div>
                <Button size="sm" variant="ghost" onClick={() => remove(t.id)} style={{ color: "#9B1C1C" }} data-testid={`task-delete-${t.id}`}>
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </li>
            );
          })}
        </ul>
      )}

      <TaskDialog open={open} onClose={() => setOpen(false)} onSaved={fetchAll} />
    </div>
  );
}
